// frontend/src/components/admin/modals/TournamentDetailsModal.jsx
import React, { useState, useEffect } from 'react';
import axios from '../../../config/axios';
import { X, Users, Trophy, Coins } from 'lucide-react';

const TournamentDetailsModal = ({ tournamentId, onClose }) => {
    const [tournament, setTournament] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchDetails = async () => {
            try {
                const response = await axios.get(`/admin/tournaments/${tournamentId}/details`);
                if (response.data.success) {
                    setTournament(response.data.tournament);
                }
            } catch (error) {
                setError(error.response?.data?.message || 'Error al cargar detalles');
            } finally {
                setLoading(false);
            }
        };

        fetchDetails();
    }, [tournamentId]);

    const formatDate = (date) => {
        return new Date(date).toLocaleString('es-AR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
                    <p>Cargando detalles...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
                    <p className="text-red-500">{error}</p>
                    <button
                        onClick={onClose}
                        className="mt-4 px-4 py-2 bg-gray-100 rounded-md"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        );
    }

    if (!tournament) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full m-4">
                {/* Header */}
                <div className="flex justify-between items-start">
                    <h2 className="text-xl font-bold text-primary">{tournament.name}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Información general */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <Trophy className="w-5 h-5 text-primary mr-2" />
                            <span className="text-sm font-medium">Premio</span>
                        </div>
                        <p className="mt-1 text-xl font-bold">{tournament.prizePool} tickets</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <Users className="w-5 h-5 text-primary mr-2" />
                            <span className="text-sm font-medium">Jugadores</span>
                        </div>
                        <p className="mt-1 text-xl font-bold">{tournament._count.players}/{tournament.maxPlayers}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <Coins className="w-5 h-5 text-primary mr-2" />
                            <span className="text-sm font-medium">Entrada</span>
                        </div>
                        <p className="mt-1 text-xl font-bold">{tournament.entryFee} tickets</p>
                    </div>
                </div>

                {/* Detalles adicionales */}
                <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Detalles del Torneo</h3>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                        <p><span className="font-medium">Inicio:</span> {formatDate(tournament.startTime)}</p>
                        <p><span className="font-medium">Estado:</span> {tournament.status}</p>
                        <p><span className="font-medium">Juego:</span> {tournament.gameType}</p>
                        <p><span className="font-medium">Jugadores mínimos:</span> {tournament.minPlayers}</p>
                    </div>
                </div>

                {/* Lista de participantes */}
                <div className="mt-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Participantes</h3>
                    <div className="bg-gray-50 rounded-lg overflow-hidden">
                        {tournament.players.length > 0 ? (
                            <div className="divide-y divide-gray-200">
                                {tournament.players.map((registration) => (
                                    <div key={registration.id} className="p-4 flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 bg-primary rounded-full flex items-center justify-center">
                                                <span className="text-white font-medium">
                                                    {registration.user.username[0].toUpperCase()}
                                                </span>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm font-medium text-gray-900">{registration.user.username}</p>
                                                <p className="text-sm text-gray-500">{registration.user.email}</p>
                                            </div>
                                        </div>
                                        {registration.isReady && (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                Listo
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4 text-center text-gray-500">
                                No hay participantes registrados aún
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TournamentDetailsModal;